import axios, { AxiosError, AxiosInstance } from 'axios';

const API_BASE_URL = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

const STORAGE_KEY = 'jasrhub_access_token';

// PR-STEP2-3 — the operational project the operator is currently inside,
// chosen on the three-tile home. Sent on every request as X-Project-Id so the
// backend scopes every per-project list to this box. null = All / owner view.
const PROJECT_KEY = 'jasrhub_current_project';

export function getStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function setStoredToken(token: string | null) {
  if (token) {
    localStorage.setItem(STORAGE_KEY, token);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function getCurrentProjectId(): string | null {
  return localStorage.getItem(PROJECT_KEY);
}

export function setCurrentProjectId(id: string | null) {
  if (id) {
    localStorage.setItem(PROJECT_KEY, id);
  } else {
    localStorage.removeItem(PROJECT_KEY);
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Inject JWT into every outgoing request
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // PR-STEP2-3 — carry the active project box on every request.
  const projectId = getCurrentProjectId();
  if (projectId) {
    config.headers['X-Project-Id'] = projectId;
  }

  // A file upload must not inherit the instance-wide JSON content type.
  // axios's default transformRequest reads:
  //
  //   if (isFormData) return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data;
  //
  // so with Content-Type: application/json still set, a FormData body is
  // silently flattened to JSON and the file never leaves the browser. The
  // server then answers "the request is not multipart" (406). Clearing the
  // header lets the browser set multipart/form-data with its boundary.
  //
  // Done here rather than at each call site so a new upload cannot forget it.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// Auto-logout on 401
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      setStoredToken(null);
      // Only redirect if we're not already on the login page (avoid loops)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export type ApiError = {
  error: string;
  message?: string;
  details?: unknown;
};

export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const apiErr = err.response?.data as ApiError | undefined;
    return apiErr?.message ?? apiErr?.error ?? err.message ?? 'Network error';
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

// PR-MAY17-MAINTENANCE-PRO Part 0 — pull Zod field-level errors out
// of the 400 response so the form can render them inline next to the
// offending input. Returns an empty object on non-axios errors or 4xx
// responses that don't include details.fieldErrors. The shape matches
// Zod's flatten().fieldErrors output: { fieldName: ["msg1", "msg2"] }.
export function extractFieldErrors(err: unknown): Record<string, string[]> {
  if (!axios.isAxiosError(err)) return {};
  const apiErr = err.response?.data as ApiError | undefined;
  const details = apiErr?.details as { fieldErrors?: Record<string, string[]> } | undefined;
  return details?.fieldErrors ?? {};
}

// PR-MAY15-INVESTOR-RATES-DRAWER item 3 — structured error extractor
// for MISSING_INVESTOR_RATE. Used by trip-create call-sites so they can
// render a "Set rate now" link directly to the investor drawer.
//
// PR-MAY16-RATE-DROPDOWN-FIX — payload now includes siteId (the new
// truth) plus siteContractId (kept for back-compat).
export function extractMissingRateAction(err: unknown): {
  message: string;
  actionUrl: string | null;
  investorId: string | null;
  vehicleId: string | null;
  siteId: string | null;
  siteContractId: string | null;
} | null {
  if (!axios.isAxiosError(err)) return null;
  const apiErr = err.response?.data as ApiError | undefined;
  if (apiErr?.error !== 'MISSING_INVESTOR_RATE') return null;
  const d = (apiErr.details ?? {}) as {
    actionUrl?: string; investorId?: string; vehicleId?: string;
    siteId?: string; siteContractId?: string;
  };
  return {
    message: apiErr.message ?? 'No investor rate configured for this site/vehicle.',
    actionUrl:      d.actionUrl      ?? null,
    investorId:     d.investorId     ?? null,
    vehicleId:      d.vehicleId      ?? null,
    siteId:         d.siteId         ?? null,
    siteContractId: d.siteContractId ?? null,
  };
}
